import React, { useCallback } from "react";
import * as XLSX from "xlsx";

import MainPageTableHeader from "../../layout/body/MainPageTableHeader";
import MainPageTableBody from "../../layout/body/MainPageTableBody";
import MainPageHOC from "../../layout/hoc/MainPageHOC";

import { PaginationComponent } from "../../components/Component";
import FetcherComponent from "../../components/shared/FetcherComponent";
import MainPageHeader from "../../components/shared/MainPageHeader";

import employeeService from "../../services/employeeService";
import { useEmployeePageContext } from "../../hooks/useEmployeePageContext";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import {
  EMPLOYEE_STATUSES,
  SEARCH_PARAM_KEY,
} from "../../utilities/constants";

const EmployeesPage = () => {
  const {
    employees,
    setEmployees,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalRecords,
    setTotalRecords,
    setPageSize,
    pageSize,
  } = useEmployeePageContext();
  const [onFetch] = useBeManualFetcher();

  const { search, setSearch, searchQuery, searchParams } = useUrlFilters({
    searchParamKey: SEARCH_PARAM_KEY,
  });

  const currentPageQueryString = searchParams.get("current-page");
  const pageSizeQueryString = searchParams.get("page-size");
  const fetchUsers = useCallback(
    async () =>
      await employeeService.getEmployees({
        currentPage: currentPageQueryString,
        pageSize: pageSizeQueryString,
        search: searchQuery,
      }),
    [currentPageQueryString, pageSizeQueryString, searchQuery]
  );

  const handleOnLoadFetchedUsers = useCallback(
    (res) => {
      setEmployees(res?.data);
      setCurrentPage(res?.currentPage);
      setTotalPages(res?.totalPages);
      setTotalRecords(res?.totalRecords);
      setPageSize(res?.pageSize);
    },
    [setEmployees, setCurrentPage, setTotalPages, setTotalRecords, setPageSize]
  );

  const employeeDataChecker = useCallback(
    () => (employees?.length > 0 ? true : false),
    [employees]
  );

  const onExportClick = useCallback(async () => {
    const employeesToExport = employees?.filter(
      (employee) => employee?.check === true
    );
    const newWorkBook = XLSX.utils.book_new();
    const newWorkSheet = XLSX.utils.json_to_sheet(employeesToExport);

    XLSX.utils.book_append_sheet(newWorkBook, newWorkSheet, "NewSheet1");

    // Fayl statusga bog'liq emas — status o'zgarmasa ham Excel yuklanadi.
    XLSX.writeFile(newWorkBook, "NewFile.xlsx");

    return await onFetch({
      action: () => employeeService.markEmployeesAsViewed(employeesToExport),
      onLoad: (viewedIds) => {
        setEmployees((prev) =>
          prev?.map((employee) => ({
            ...employee,
            check: false,
            ...(viewedIds.includes(employee.id) && {
              status: EMPLOYEE_STATUSES.VIEWED,
              statusReason: null,
            }),
          }))
        );
      },
      successMessage: "Excel file successfully generated",
    });
  }, [employees, onFetch, setEmployees]);

  const tableHeader = React.useMemo(
    () => (
      <MainPageTableHeader
        pageSize={pageSize}
        setPageSize={setPageSize}
        searchValue={search}
        onSearchChange={setSearch}
      />
    ),
    [pageSize, setPageSize, search, setSearch]
  );

  const tableBody = React.useMemo(
    () => (
      <FetcherComponent
        key={[currentPageQueryString, pageSizeQueryString, searchQuery]
          .filter(Boolean)
          .join("-")}
        action={fetchUsers}
        onLoad={handleOnLoadFetchedUsers}
      >
        <>
          <MainPageTableBody
            pageName="employees"
            users={employees}
            setUsers={setEmployees}
          />
          <div className="card-inner">
            <PaginationComponent
              currentPageNumber={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              status={pageSizeQueryString}
            />
          </div>
        </>
      </FetcherComponent>
    ),
    [
      currentPageQueryString,
      pageSizeQueryString,
      searchQuery,
      fetchUsers,
      handleOnLoadFetchedUsers,
      employees,
      setEmployees,
      currentPage,
      totalPages,
      totalRecords,
    ]
  );

  return (
    <MainPageHOC
      headerTitle={"Employees"}
      pageHeader={
        <MainPageHeader
          totalRecords={totalRecords}
          pageName="Employees"
          onExportClick={onExportClick}
          disableExportButton={employees?.find(
            (employee) => employee?.check === true
          )}
          showExportButton={true}
        />
      }
      tableHeader={tableHeader}
      tableBody={tableBody}
      hasDataForRendering={employeeDataChecker}
    />
  );
};
export default EmployeesPage;
